import type {Source} from "@/domain/type/source.type.ts";
import {SynchroType} from "@/domain/type/synchro.type.ts";

export type DemandResponse = {
  requestUuid?: string
  error?: string
}

export type GetEntityResponse = {
  uuid: string,
  state: RequestEntityState
  status: RequestEntityStatus
  requestType: SynchroType,
  entity: SourceDetails[],
  matchingEntities: SourceDetails[][],
  fields: SynchroRequestField[],
  src1Id: string,
  src2Id: string,
}

export class RequestEntity implements GetEntityResponse {
  public uuid: string;
  public state: RequestEntityState;
  public status: RequestEntityStatus;
  public requestType: SynchroType;
  public entity: SourceDetails[];
  public matchingEntities: SourceDetails[][];
  public fields: SynchroRequestField[];
  public src1Id: string;
  public src2Id: string;

  constructor(response: GetEntityResponse) {
    this.uuid = response.uuid;
    this.state = response.state;
    this.status = response.status;
    this.requestType = response.requestType;
    this.entity = response.entity;
    this.matchingEntities = response.matchingEntities;
    this.fields = response.fields;
    this.src1Id = response.src1Id;
    this.src2Id = response.src2Id;
  }

  public isDisallowed(): boolean {
    return this.status === RequestEntityStatus.DISALLOWED;
  }

  public isReadOnly(): boolean {
    const allowedTypes = [
      SynchroType.COURSE,
      SynchroType.PERSONS,
      SynchroType.COMPANIES,
      SynchroType.PRODUCT,
      SynchroType.LOCATION,
      SynchroType.SESSIONS,
    ];
    return !allowedTypes.includes(this.requestType)
  }
}

export type GetRequestResponse = {
  uuid: string,
  requestType: SynchroType,
  src1: Source,
  src2: Source,
  entities: GetEntityResponse[]
}
export type GetEntityFieldsResponse = {
  src1: Source,
  src2: Source,
  block: {
    details: BlockDetails,
    groupUid: string,
    requestList: SynchroRequestFieldList
    status: RequestFieldStatus
  }
}
export type BlockDetails = {
  src1: SourceDetails[]
  src2: SourceDetails[]
}
export type SourceDetails = {
  label: string,
  val: string | number
}
export type SynchroRequestField = {
  isDisallowed: boolean,
  isOptionnel: boolean,
  isSynchronized: boolean,
  isSensitive: boolean,
  readOnlySrc1Info: string,
  readOnlySrc2Info: string,
  label: string,
  readOnlySrc1: boolean,
  readOnlySrc2: boolean,
  synchroRequestId: number,
  synchroStatus: number,
  valueSrc1: string,
  valueSrc2: string,
  displayValueSrc1?: string | null,
  displayValueSrc2?: string | null,
}

export interface SynchroRequestFieldList {
  [id: string]: SynchroRequestField
}

export enum RequestEntityState {
  TOO_MANY = 'tooMany',
  NOT_ANY = 'missing',
  MATCH = 'match',
  MATCH_NOT_SIMILAR = 'matchNotSimilar',
}

export enum RequestEntityStatus {
  SYNCHRONIZED = 'synchronized',
  TO_SYNC = 'toSync',
  OPTIONAL_TO_SYNC = 'optionalToSync',
  NOT_ALLOWED_TO_SYNC = 'notAllowedToSync',
  DISALLOWED = 'disallowed'
}

export enum RequestFieldStatus {
  UNEXPECTED = -1,
  SYNCHRONIZED = 1,
  TO_SYNCHRONIZE = 20,
  ACCEPTED = 15,
  COMPLETED = 20,
  DISCARDED = 30,
  DISALLOWED = 40,
  OPTIONAL_TO_SYNCHRONIZE = 50,
  OPTIONAL_SYNCHRONIZED = 60,
}
